/**
 * Branded identifiers.
 *
 * The schema is full of uuid columns — `team_a_id`, `striker_id`, `bowler_id`,
 * `player_out_id`, `created_by_user_id`. Plain `string` everywhere means the
 * compiler happily accepts a TeamId where a PlayerId belongs, and the symptom is
 * a wrong scorecard rather than a crash. Branding costs a cast at the database
 * and HTTP boundaries and catches that class at compile time.
 */

declare const brand: unique symbol;

type Branded<T, B extends string> = T & { readonly [brand]: B };

export type UserId = Branded<string, "UserId">;
export type PlayerId = Branded<string, "PlayerId">;
export type TeamId = Branded<string, "TeamId">;
export type MatchId = Branded<string, "MatchId">;
export type EventId = Branded<string, "EventId">;
export type RuleSetId = Branded<string, "RuleSetId">;
export type DeviceId = Branded<string, "DeviceId">;
export type ClipId = Branded<string, "ClipId">;

export type TournamentId = Branded<string, "TournamentId">;

/** The short public identifier in `/m/{shortcode}` or `/t/{shortcode}`. Distinct from `MatchId`/`TournamentId`. */
export type Shortcode = Branded<string, "Shortcode">;

/**
 * Cast a raw string into a branded id.
 *
 * Deliberately unvalidated and deliberately explicit: it marks the boundary where
 * an untrusted string becomes a domain id, so those boundaries are greppable. Use
 * it when reading a database row or parsing a request, not inside domain code.
 */
export const asId = <T extends Branded<string, string>>(raw: string): T => raw as T;

/**
 * A 1-based innings number.
 *
 * Deliberately `number` rather than a literal union. It was `1 | 2 | 3 | 4`, which
 * made a super over unrepresentable: a tied T20 puts the super over at innings 5
 * and 6, and PRD §6.4 requires support for a *second* super over, so any fixed
 * ceiling is wrong. Reusing 3 and 4 is not an option either — `seq` is unique per
 * `(match_id, innings_no, seq)`, so a second super over would collide with the
 * first.
 *
 * Little is lost: unlike the id types above, an innings number is not confusable
 * with another domain concept, so the literal union bought range checking rather
 * than type safety. Range is enforced where it belongs, at the boundary.
 */
export type InningsNo = number;
